import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, RefreshCw, Loader2, AlertTriangle, Send, CheckCircle2,
  Play, Clock,
} from 'lucide-react';

export default function WhatsAppTemplates() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [testDialog, setTestDialog] = useState(null);  // selected template
  const [testForm, setTestForm] = useState({ to: '', params: [] });
  const [sending, setSending] = useState(false);
  const [runningRsvp, setRunningRsvp] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [lastBillingRun, setLastBillingRun] = useState(null);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/whatsapp/templates');
      if (data.success === false) {
        setError(data.error || 'Failed to load templates');
        setTemplates([]);
      } else {
        setError(null);
        setTemplates(data.templates || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  // Count placeholders in BODY component
  const countBodyParams = (tpl) => {
    const body = (tpl?.components || []).find((c) => c.type === 'BODY');
    if (!body?.text) return 0;
    const matches = body.text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const getBodyText = (tpl) => {
    const body = (tpl?.components || []).find((c) => c.type === 'BODY');
    return body?.text || '(no body)';
  };

  const openTest = (tpl) => {
    const n = countBodyParams(tpl);
    setTestForm({ to: '', params: Array(n).fill('') });
    setTestDialog(tpl);
  };

  const sendTest = async () => {
    if (!testForm.to.trim()) {
      toast.error('Recipient phone required');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/whatsapp/send-template', {
        to: testForm.to.trim(),
        template_name: testDialog.name,
        language: testDialog.language,
        body_params: testForm.params,
      });
      if (data.success) {
        toast.success(`Sent! message_id=${data.message_id?.slice(-12)}`);
        setTestDialog(null);
      } else {
        toast.error(data.error || 'Send failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const runRsvpReminders = async () => {
    setRunningRsvp(true);
    try {
      const { data } = await api.post('/whatsapp/automations/rsvp-reminder/run');
      setLastRun({ ...data, at: new Date().toLocaleTimeString() });
      toast.success(`RSVP reminders: sent=${data.sent}, skipped=${data.skipped}`);
    } catch (e) {
      toast.error('Run failed');
    } finally {
      setRunningRsvp(false);
    }
  };

  const runBillingReminders = async () => {
    setRunningBilling(true);
    try {
      const { data } = await api.post('/whatsapp/automations/billing-reminder/run');
      setLastBillingRun({ ...data, at: new Date().toLocaleTimeString() });
      toast.success(`Billing reminders: sent=${data.sent}, skipped=${data.skipped}`);
    } catch (e) {
      toast.error('Run failed');
    } finally {
      setRunningBilling(false);
    }
  };

  const statusColor = (s) => {
    switch ((s || '').toUpperCase()) {
      case 'APPROVED': return 'bg-green-100 text-green-800 border-green-300';
      case 'PENDING': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-5xl" data-testid="whatsapp-templates-page">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 h-7 px-2" onClick={() => navigate('/admin/whatsapp')} data-testid="back-to-inbox-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Inbox
          </Button>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
                <FileText className="w-6 h-6 text-green-600" />
                WhatsApp Templates
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Live from Meta — these templates can be used for business-initiated messages outside the 24h window.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={loadTemplates} data-testid="refresh-templates-btn">
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/20" data-testid="templates-error">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
              {error}
              <div className="mt-1">Ensure WhatsApp Business Account ID is set in <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/whatsapp/settings'); }} className="underline">Settings</a>.</div>
            </AlertDescription>
          </Alert>
        )}

        {/* Automation control */}
        <Card data-testid="automation-card">
          <CardHeader className="p-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Play className="w-4 h-4" />
              Automations
            </CardTitle>
            <CardDescription className="text-xs">
              Scheduled WhatsApp nudges. Runs automatically; use the button below to trigger a pass immediately.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="border rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> RSVP Reminder
                  <Badge variant="outline" className="text-[10px] py-0 h-4">Hourly</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  24–36h before a game, nudge parents of registered players who haven't RSVP'd.
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Uses template: <code>game_rsvp_notification</code> (live in Meta)
                </div>
              </div>
              <div className="flex items-center gap-2">
                {lastRun && (
                  <span className="text-[11px] text-muted-foreground">
                    Last: sent={lastRun.sent}, skipped={lastRun.skipped} · {lastRun.at}
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={runRsvpReminders} disabled={runningRsvp} data-testid="run-rsvp-reminders-btn">
                  {runningRsvp ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  Run Now
                </Button>
              </div>
            </div>

            <div className="border rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> Billing Reminder
                  <Badge variant="outline" className="text-[10px] py-0 h-4">Daily</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Nudge parents with overdue enrollments, lane bookings, or tournament dues. Deduped per parent (3-day window).
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Uses template: <code>billing_reminder</code> (submitted to Meta — pending approval)
                </div>
              </div>
              <div className="flex items-center gap-2">
                {lastBillingRun && (
                  <span className="text-[11px] text-muted-foreground">
                    Last: sent={lastBillingRun.sent}, skipped={lastBillingRun.skipped} · {lastBillingRun.at}
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={runBillingReminders} disabled={runningBilling} data-testid="run-billing-reminders-btn">
                  {runningBilling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  Run Now
                </Button>
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Booking Confirmation
                <Badge variant="outline" className="text-[10px] py-0 h-4">On-event</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Sent automatically to parents when staff/admin approves an enrollment.
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Uses template: <code>player_enrollment_confimed</code> (submitted to Meta — pending approval)
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Lane Booking Confirmation
                <Badge variant="outline" className="text-[10px] py-0 h-4">On-event</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Sent automatically when a parent books a lane (paid or pay-with-credit).
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Uses template: <code>lane_booking_confirmed</code> (submitted to Meta — pending approval)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates list */}
        <Card>
          <CardHeader className="p-4">
            <CardTitle className="text-base">Templates ({templates.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="p-6 text-center text-muted-foreground text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
              </div>
            )}
            {!loading && templates.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="empty-templates">
                No templates found. Create and get approval in Meta Business Manager → WhatsApp Manager → Message Templates.
              </div>
            )}
            {!loading && templates.map((t) => (
              <div key={`${t.name}-${t.language}`} className="p-3 border-b hover:bg-accent/30 transition-colors" data-testid={`template-row-${t.name}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-semibold text-sm">{t.name}</code>
                      <Badge variant="outline" className="text-[10px] py-0 h-4">{t.language}</Badge>
                      <Badge variant="outline" className={`text-[10px] py-0 h-4 ${statusColor(t.status)}`}>
                        {t.status === 'APPROVED' && <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />}
                        {t.status}
                      </Badge>
                      {t.category && <Badge variant="outline" className="text-[10px] py-0 h-4">{t.category}</Badge>}
                      {countBodyParams(t) > 0 && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4">{countBodyParams(t)} params</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap max-w-2xl">
                      {getBodyText(t)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openTest(t)}
                    disabled={t.status !== 'APPROVED'}
                    data-testid={`send-test-${t.name}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    Send Test
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Test send dialog */}
      <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
        <DialogContent data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Send Test: {testDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipient (E.164 format)</Label>
              <Input
                placeholder="+17322439348"
                value={testForm.to}
                onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
                className="mt-1 font-mono text-sm"
                data-testid="test-to-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Must be on Meta's allow-list if app is in Development mode.
              </p>
            </div>
            {testForm.params.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs">Body parameters</Label>
                {testForm.params.map((val, i) => (
                  <Input
                    key={i}
                    placeholder={`{{${i + 1}}}`}
                    value={val}
                    onChange={(e) => {
                      const next = [...testForm.params];
                      next[i] = e.target.value;
                      setTestForm({ ...testForm, params: next });
                    }}
                    className="font-mono text-sm"
                    data-testid={`test-param-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog(null)}>Cancel</Button>
            <Button onClick={sendTest} disabled={sending} data-testid="send-test-confirm-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
